/**
 * Studio navigation: hover/focus panels on desktop, an expanding panel on mobile.
 */
class EmeNav extends HTMLElement {
  /** @type {number | undefined} */
  #closeTimer;

  /** @type {HTMLElement | null} */
  #activeLink = null;

  connectedCallback() {
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get #isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (!this.#isDesktop || event.pointerType === 'touch') return;
    const target = /** @type {HTMLElement} */ (event.target);

    clearTimeout(this.#closeTimer);

    const link = /** @type {HTMLElement | null} */ (target.closest('.eme-nav__link'));
    if (link) {
      if (link.dataset.panel) {
        this.#openPanel(link);
      } else {
        this.#closePanels();
      }
    }
  };

  #onPointerLeave = () => {
    if (!this.#isDesktop) return;
    this.#closeTimer = window.setTimeout(() => this.#closePanels(), 150);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!this.#isDesktop) return;
    const link = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest('.eme-nav__link'));
    if (!link) return;
    if (link.dataset.panel) this.#openPanel(link);
    else this.#closePanels();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-eme-toggle]')) {
      event.preventDefault();
      this.#toggleMobile();
      return;
    }

    // On touch devices at desktop width, the first tap opens the panel instead of navigating
    const link = /** @type {HTMLElement | null} */ (target.closest('.eme-nav__link[data-panel]'));
    if (link && this.#isDesktop && link !== this.#activeLink) {
      event.preventDefault();
      this.#openPanel(link);
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) {
      this.#closePanels();
      this.#toggleMobile(false);
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#activeLink) {
      const link = this.#activeLink;
      this.#closePanels();
      link.focus();
    }
    if (this.hasAttribute('data-mobile-open')) {
      this.#toggleMobile(false);
      /** @type {HTMLElement | null} */ (this.querySelector('[data-eme-toggle]'))?.focus();
    }
  };

  /** @param {HTMLElement} link */
  #openPanel(link) {
    if (this.#activeLink === link) return;
    this.#closePanels();

    const panel = document.getElementById(link.dataset.panel ?? '');
    if (!panel) return;

    panel.hidden = false;
    link.setAttribute('aria-expanded', 'true');
    this.#activeLink = link;
    this.setAttribute('data-panel-open', '');
  }

  #closePanels() {
    for (const panel of this.querySelectorAll('[data-eme-panel]')) {
      /** @type {HTMLElement} */ (panel).hidden = true;
    }
    for (const link of this.querySelectorAll('.eme-nav__link[aria-expanded="true"]')) {
      link.setAttribute('aria-expanded', 'false');
    }
    this.#activeLink = null;
    if (!this.hasAttribute('data-mobile-open')) this.removeAttribute('data-panel-open');
  }

  /** @param {boolean} [force] */
  #toggleMobile(force) {
    const panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-eme-mobile]'));
    const toggle = this.querySelector('[data-eme-toggle]');
    if (!panel) return;

    const open = force ?? panel.hidden;
    panel.hidden = !open;
    toggle?.setAttribute('aria-expanded', String(open));
    this.toggleAttribute('data-mobile-open', open);
    this.toggleAttribute('data-panel-open', open || this.#activeLink !== null);
  }
}

if (!customElements.get('eme-nav')) {
  customElements.define('eme-nav', EmeNav);
}
